/** Multi-series monthly income chart for Analytics Total Performance. */

export type IncomeChartSeriesId =
  | "premiums"
  | "assignIncome"
  | "btcCost"
  | "marginCost"
  | "income"
  | "ownCapital"
  | "rocPct"
  | "spxPct";

export type IncomeChartAxis = "usd" | "usdEom" | "pct";

export interface IncomeChartMonthPoint {
  month: string;
  label: string;
  shortLabel: string;
  premiums: number;
  assignIncome: number;
  /** Always ≥ 0 — plotted as a cost magnitude so the dollar axis stays above zero. */
  btcCost: number;
  /** Always ≥ 0 — plotted as a cost magnitude so the dollar axis stays above zero. */
  marginCost: number;
  income: number;
  /** Own capital at month-end (same as the Total Own Capital KPI). */
  ownCapital: number;
  /** Net income after margin ÷ prior-month own capital (same as month-card ROC). */
  rocPct: number | null;
  /** S&P 500 monthly total return (SPY, dividend-adjusted). Null when unknown. */
  spxPct: number | null;
  forecast: boolean;
}

export const INCOME_CHART_SERIES: ReadonlyArray<{
  id: IncomeChartSeriesId;
  label: string;
  color: string;
  width: number;
  axis: IncomeChartAxis;
  dash?: string;
}> = [
  { id: "income", label: "Income", color: "#ff8c00", width: 2.6, axis: "usd" },
  { id: "premiums", label: "Premiums", color: "#facc15", width: 1.7, axis: "usd" },
  { id: "rocPct", label: "ROC %", color: "#3b82f6", width: 2, axis: "pct", dash: "1.6 3.6" },
  { id: "btcCost", label: "BTC cost", color: "#b91c1c", width: 1.7, axis: "usd" },
  { id: "marginCost", label: "Margin cost", color: "#ff3b30", width: 1.7, axis: "usd" },
  { id: "ownCapital", label: "Own capital", color: "#ffffff", width: 2.8, axis: "usdEom" },
  { id: "assignIncome", label: "Call assgn.", color: "#22d3ee", width: 1.7, axis: "usd" },
  { id: "spxPct", label: "S&P 500", color: "#c084fc", width: 2, axis: "pct", dash: "1.6 3.6" },
];

const CHART_TIP =
  "The shaded area is the forecast. Call assignment includes expected assignment of open calls and can be negative, so the dollar axis extends below zero. Premiums there is planned income; BTC cost forecasts at zero. Predicted margin, target income, and planned own capital are included. Dollar lines are solid. ROC % and S&P 500 are dotted and share the right axis. S&P 500 holds the last closed month flat through the forecast, only so the line continues. Hover a month for exact values. Click a series in the legend to hide it.";

const HIDDEN_SERIES_KEY = "sfe-income-chart-hidden-series";
const VIEW_W = 760;
const VIEW_H_DESKTOP = 228;
/** ~30% below the prior 360px stretched box; tuned so ~390px phone width yields ~252px. */
const VIEW_H_MOBILE = 490;
const PAD = { l: 72, r: 44, t: 12, b: 30 };

/** Active viewBox height for the current render (desktop vs compact). */
let viewH = VIEW_H_DESKTOP;

function isCompactChartViewport(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(max-width: 720px)").matches;
}

function resolveViewH(): number {
  return isCompactChartViewport() ? VIEW_H_MOBILE : VIEW_H_DESKTOP;
}

function esc(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function fmtUsdWhole(value: number): string {
  const sign = value < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(value)).toLocaleString("en-US", {
    maximumFractionDigits: 0,
  })}`;
}

function fmtAxisUsd(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "−" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1000) return `${sign}$${Math.round(abs / 1000)}k`;
  return `${sign}$${Math.round(abs)}`;
}

function fmtAxisPct(value: number): string {
  const sign = value < 0 ? "−" : "";
  const abs = Math.abs(value);
  if (abs >= 10 || Number.isInteger(value)) return `${sign}${Math.round(abs)}%`;
  return `${sign}${abs.toFixed(1)}%`;
}

function fmtRocPct(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  const sign = value < 0 ? "-" : "";
  return `${sign}${Math.abs(value).toFixed(2)}%`;
}

function readHiddenSeries(): Set<IncomeChartSeriesId> {
  try {
    const raw = localStorage.getItem(HIDDEN_SERIES_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return new Set();
    const allowed = new Set(INCOME_CHART_SERIES.map((s) => s.id));
    return new Set(
      parsed.filter((id): id is IncomeChartSeriesId => allowed.has(id as IncomeChartSeriesId)),
    );
  } catch {
    return new Set();
  }
}

function writeHiddenSeries(hidden: Set<IncomeChartSeriesId>): void {
  try {
    localStorage.setItem(HIDDEN_SERIES_KEY, JSON.stringify([...hidden]));
  } catch {
    /* ignore */
  }
}

function seriesValue(point: IncomeChartMonthPoint, id: IncomeChartSeriesId): number | null {
  if (id === "rocPct" || id === "spxPct") {
    const value = point[id];
    return value != null && Number.isFinite(value) ? value : null;
  }
  if (id === "ownCapital") {
    const value = point.ownCapital;
    return value > 0 && Number.isFinite(value) ? value : null;
  }
  const value = point[id];
  return value != null && Number.isFinite(value) ? value : null;
}

function firstForecastIndex(points: IncomeChartMonthPoint[]): number {
  return points.findIndex((point) => point.forecast);
}

function niceCeil(abs: number): number {
  if (!(abs > 0) || !Number.isFinite(abs)) return 1;
  const pow = 10 ** Math.floor(Math.log10(abs));
  const n = abs / pow;
  const nice = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return nice * pow;
}

function yDomain(
  points: IncomeChartMonthPoint[],
  hidden: Set<IncomeChartSeriesId>,
  axis: IncomeChartAxis,
): { min: number; max: number } {
  let min = 0;
  let max = 0;
  let seen = false;
  for (const point of points) {
    for (const series of INCOME_CHART_SERIES) {
      if (series.axis !== axis || hidden.has(series.id)) continue;
      const value = seriesValue(point, series.id);
      if (value == null) continue;
      seen = true;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  if (axis === "pct") {
    if (!seen) return { min: -5, max: 15 };
    const span = Math.max(max - min, 4);
    const pad = Math.max(1, span * 0.1);
    // Fit the scale to the series, with a small negative band so a down month
    // is visible. Do not hold the axis open to 25 or a large unused floor.
    const lo = Math.min(min - pad, -5);
    const hi = max + pad;
    const step = 5;
    return {
      min: Math.floor(lo / step) * step,
      max: Math.ceil((hi - 1e-9) / step) * step,
    };
  }
  if (!seen || (min === 0 && max === 0)) return { min: -1, max: 1 };
  if (axis === "usdEom") {
    const span = Math.max(max - min, Math.max(Math.abs(max), Math.abs(min)) * 0.06, 1000);
    const pad = span * 0.15;
    return { min: min - pad, max: max + pad };
  }
  const pad = Math.max(Math.abs(min), Math.abs(max)) * 0.08;
  // Call assignment can be a loss. Keep a negative band so that dip is visible
  // under the much larger premium bars.
  min = min < 0 ? -niceCeil(Math.abs(min) + pad) : 0;
  max = max > 0 ? niceCeil(max + pad) : 0;
  if (min === 0 && max === 0) return { min: -1, max: 1 };
  return { min, max };
}

function axisVisible(hidden: Set<IncomeChartSeriesId>, axis: IncomeChartAxis): boolean {
  return INCOME_CHART_SERIES.some((series) => series.axis === axis && !hidden.has(series.id));
}

function pctTicksFor(min: number, max: number): number[] {
  const span = max - min;
  const step = span > 40 ? 10 : 5;
  const ticks: number[] = [];
  const start = Math.ceil(min / step) * step;
  for (let value = start; value <= max + 1e-9; value += step) ticks.push(value);
  if (min < 0 && max > 0 && !ticks.includes(0)) ticks.push(0);
  return ticks.sort((a, b) => a - b);
}

function yTicks(min: number, max: number): number[] {
  const ticks = new Set<number>([min, max]);
  if (min < 0 && max > 0) ticks.add(0);
  const span = max - min;
  const step = niceCeil(span / 4);
  if (step > 0) {
    for (let value = Math.ceil(min / step) * step; value < max - step * 0.05; value += step) {
      if (Math.abs(value) > 1e-9) ticks.add(value);
    }
  }
  return [...ticks].sort((a, b) => a - b);
}

function plotBox(): { x: number; y: number; w: number; h: number } {
  return {
    x: PAD.l,
    y: PAD.t,
    w: VIEW_W - PAD.l - PAD.r,
    h: viewH - PAD.t - PAD.b,
  };
}

function xAt(index: number, count: number): number {
  const box = plotBox();
  if (count <= 1) return box.x + box.w / 2;
  return box.x + (index / (count - 1)) * box.w;
}

function yAt(value: number, min: number, max: number): number {
  const box = plotBox();
  const span = max - min || 1;
  return box.y + box.h - ((value - min) / span) * box.h;
}

function seriesPath(
  points: IncomeChartMonthPoint[],
  id: IncomeChartSeriesId,
  min: number,
  max: number,
): string {
  const parts: string[] = [];
  let drawing = false;
  points.forEach((point, index) => {
    const value = seriesValue(point, id);
    if (value == null) {
      drawing = false;
      return;
    }
    const x = xAt(index, points.length).toFixed(1);
    const y = yAt(value, min, max).toFixed(1);
    parts.push(`${drawing ? "L" : "M"}${x} ${y}`);
    drawing = true;
  });
  return parts.join(" ");
}

function xLabelStep(count: number): number {
  if (count <= 12) return 1;
  if (count <= 18) return 2;
  return 3;
}

/** Evenly spaced month labels, including the first and last so a forecast month
 * is not drawn on top of the month before it. */
function xLabelIndices(count: number): Set<number> {
  const indices = new Set<number>();
  if (count <= 0) return indices;
  if (count === 1) return new Set([0]);
  const step = xLabelStep(count);
  const slots = Math.max(1, Math.round((count - 1) / step));
  for (let i = 0; i <= slots; i += 1) {
    indices.add(Math.round((i * (count - 1)) / slots));
  }
  return indices;
}

function renderSvg(
  points: IncomeChartMonthPoint[],
  selectedMonth: string | null,
  hidden: Set<IncomeChartSeriesId>,
): string {
  viewH = resolveViewH();
  const usd = yDomain(points, hidden, "usd");
  const usdEom = yDomain(points, hidden, "usdEom");
  const pct = yDomain(points, hidden, "pct");
  const showUsd = axisVisible(hidden, "usd");
  const showPct = axisVisible(hidden, "pct");
  const box = plotBox();
  const ticks = showUsd ? yTicks(usd.min, usd.max) : [];
  const pctTicks = showPct ? pctTicksFor(pct.min, pct.max) : [];
  const labelAt = xLabelIndices(points.length);
  // Percent zero only — dollar series keep the bottom plot axis, not a $0 line.
  const zeroY =
    showPct && pct.min <= 0 && pct.max >= 0 ? yAt(0, pct.min, pct.max) : null;
  const selectedIndex = selectedMonth ? points.findIndex((p) => p.month === selectedMonth) : -1;

  const grid = ticks
    .map((tick) => {
      const y = yAt(tick, usd.min, usd.max).toFixed(1);
      return (
        `<line class="sfe-income-chart-grid" x1="${box.x}" y1="${y}" x2="${box.x + box.w}" y2="${y}"></line>` +
        `<text class="sfe-income-chart-axis" x="${box.x - 6}" y="${y}" dy="0.32em" text-anchor="end">${esc(fmtAxisUsd(tick))}</text>`
      );
    })
    .join("");

  const pctAxis = pctTicks
    .map((tick) => {
      const y = yAt(tick, pct.min, pct.max).toFixed(1);
      return `<text class="sfe-income-chart-axis sfe-income-chart-axis-pct" x="${box.x + box.w + 6}" y="${y}" dy="0.32em" text-anchor="start">${esc(fmtAxisPct(tick))}</text>`;
    })
    .join("");

  const zero =
    zeroY != null
      ? `<line class="sfe-income-chart-zero" x1="${box.x}" y1="${zeroY.toFixed(1)}" x2="${box.x + box.w}" y2="${zeroY.toFixed(1)}"></line>`
      : "";
  const xAxis = `<line class="sfe-income-chart-x-axis" x1="${box.x}" y1="${box.y + box.h}" x2="${box.x + box.w}" y2="${box.y + box.h}"></line>`;

  const selected =
    selectedIndex >= 0
      ? `<line class="sfe-income-chart-selected" x1="${xAt(selectedIndex, points.length).toFixed(1)}" y1="${box.y}" x2="${xAt(selectedIndex, points.length).toFixed(1)}" y2="${box.y + box.h}"></line>`
      : "";

  const xLabels = points
    .map((point, index) => {
      if (!labelAt.has(index)) return "";
      const x = xAt(index, points.length).toFixed(1);
      const cls = point.forecast ? "sfe-income-chart-xlabel is-forecast" : "sfe-income-chart-xlabel";
      return `<text class="${cls}" x="${x}" y="${box.y + box.h + 16}" text-anchor="middle">${esc(point.shortLabel)}</text>`;
    })
    .join("");

  const forecastAt = firstForecastIndex(points);
  const forecastMark =
    forecastAt >= 0
      ? (() => {
          const prevX = forecastAt > 0 ? xAt(forecastAt - 1, points.length) : box.x;
          const startX = forecastAt > 0 ? (prevX + xAt(forecastAt, points.length)) / 2 : xAt(forecastAt, points.length);
          const width = box.x + box.w - startX;
          if (width <= 0) return "";
          const labelX = Math.min(startX + 8, box.x + box.w - 36);
          return (
            `<rect class="sfe-income-chart-forecast-band" x="${startX.toFixed(1)}" y="${box.y}" width="${width.toFixed(1)}" height="${box.h}"></rect>` +
            `<line class="sfe-income-chart-forecast-mark" x1="${startX.toFixed(1)}" y1="${box.y}" x2="${startX.toFixed(1)}" y2="${box.y + box.h}"></line>` +
            `<text class="sfe-income-chart-forecast-label" x="${labelX.toFixed(1)}" y="${box.y + 11}">Forecast</text>`
          );
        })()
      : "";

  const seriesSvg = INCOME_CHART_SERIES.map((series) => {
    if (hidden.has(series.id)) return "";
    const scale = series.axis === "pct" ? pct : series.axis === "usdEom" ? usdEom : usd;
    const d = seriesPath(points, series.id, scale.min, scale.max);
    const dash = series.dash ? ` stroke-dasharray="${series.dash}"` : "";
    const dots = points
      .map((point, index) => {
        const value = seriesValue(point, series.id);
        if (value == null) return "";
        const cx = xAt(index, points.length).toFixed(1);
        const cy = yAt(value, scale.min, scale.max).toFixed(1);
        const r = point.month === selectedMonth ? 3.4 : 2.6;
        return `<circle class="sfe-income-chart-dot" cx="${cx}" cy="${cy}" r="${r}" fill="${series.color}" stroke="${series.color}"></circle>`;
      })
      .join("");
    return (
      `<g data-series="${series.id}">` +
      (d
        ? `<path class="sfe-income-chart-line" d="${d}" fill="none" stroke="${series.color}" stroke-width="${series.width}"${dash}></path>`
        : "") +
      dots +
      `</g>`
    );
  }).join("");

  return (
    `<svg class="sfe-income-chart-svg" viewBox="0 0 ${VIEW_W} ${viewH}" role="img" aria-label="Monthly income and return on own capital">` +
    `<rect class="sfe-income-chart-plot" x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}"></rect>` +
    grid +
    pctAxis +
    `<line class="sfe-income-chart-y-axis" x1="${box.x}" y1="${box.y}" x2="${box.x}" y2="${box.y + box.h}"></line>` +
    xAxis +
    forecastMark +
    zero +
    selected +
    seriesSvg +
    xLabels +
    `<line class="sfe-income-chart-hover-x" hidden x1="0" y1="${box.y}" x2="0" y2="${box.y + box.h}"></line>` +
    `<rect class="sfe-income-chart-hit" x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}"></rect>` +
    `</svg>`
  );
}

function legendHtml(hidden: Set<IncomeChartSeriesId>): string {
  return (
    `<div class="sfe-income-chart-legend">` +
    INCOME_CHART_SERIES.map((series) => {
      const off = hidden.has(series.id) ? " is-off" : "";
      return (
        `<button type="button" class="sfe-income-chart-legend-btn${off}" data-series="${series.id}">` +
        `<span class="sfe-income-chart-swatch" style="background:${series.color}"></span>` +
        `${esc(series.label)}` +
        `</button>`
      );
    }).join("") +
    `<span class="sfe-income-chart-legend-note">Shaded area is forecast · dotted lines are percent</span>` +
    `</div>`
  );
}

function tooltipRowsHtml(point: IncomeChartMonthPoint, hidden: Set<IncomeChartSeriesId>): string {
  return INCOME_CHART_SERIES.map((series) => {
    if (hidden.has(series.id)) return "";
    const value = seriesValue(point, series.id);
    if (value == null && series.id !== "rocPct") return "";
    const cls = value != null && value < 0 ? " is-neg" : value != null && value > 0 ? " is-pos" : "";
    const text = series.axis === "pct" ? fmtRocPct(value) : fmtUsdWhole(value ?? 0);
    return (
      `<div class="sfe-income-chart-tip-row">` +
      `<span><i style="background:${series.color}"></i>${esc(series.label)}</span>` +
      `<b class="${cls.trim()}">${text}</b>` +
      `</div>`
    );
  }).join("");
}

export function incomeChartInnerHtml(
  points: IncomeChartMonthPoint[],
  selectedMonth: string | null = null,
): string {
  viewH = resolveViewH();
  const hidden = readHiddenSeries();
  if (!points.length) {
    return `<p class="sfe-income-chart-empty muted small">No monthly income to chart.</p>`;
  }
  return (
    legendHtml(hidden) +
    `<div class="sfe-income-chart-plot-wrap">` +
    renderSvg(points, selectedMonth, hidden) +
    `<div class="sfe-income-chart-tip" hidden></div>` +
    `</div>`
  );
}

function nearestIndex(svg: SVGSVGElement, clientX: number, count: number): number {
  const hit = svg.querySelector(".sfe-income-chart-hit");
  if (!(hit instanceof SVGGraphicsElement) || count <= 0) return 0;
  const rect = hit.getBoundingClientRect();
  if (rect.width <= 0) return 0;
  const t = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
  return Math.round(t * (count - 1));
}

export function bindIncomeByMonthChart(
  root: ParentNode,
  options: {
    points: IncomeChartMonthPoint[];
    selectedMonth?: string | null;
    onSelectMonth?: ((month: string) => void) | null;
  },
): void {
  const host = root.querySelector<HTMLElement>(".sfe-income-chart");
  if (!host) return;
  const points = options.points;
  const selectedMonth = options.selectedMonth ?? null;

  const redraw = (): void => {
    host.innerHTML = incomeChartInnerHtml(points, selectedMonth);
    wirePlot();
  };

  if (host.dataset.bound !== "1") {
    host.dataset.bound = "1";
    if (!document.documentElement.dataset.sfeIncomeChartTipGuard) {
      document.documentElement.dataset.sfeIncomeChartTipGuard = "1";
      document.addEventListener("pointerdown", (event) => {
        const target = event.target;
        document.querySelectorAll(".sfe-income-chart").forEach((chart) => {
          if (target instanceof Node && chart.contains(target)) return;
          const tipEl = chart.querySelector<HTMLElement>(".sfe-income-chart-tip");
          const hoverEl = chart.querySelector<SVGLineElement>(".sfe-income-chart-hover-x");
          if (tipEl) tipEl.hidden = true;
          hoverEl?.setAttribute("hidden", "");
        });
      });
    }
    host.addEventListener("click", (event) => {
      const btn = (event.target as HTMLElement).closest<HTMLButtonElement>(
        ".sfe-income-chart-legend-btn",
      );
      if (!btn) return;
      event.preventDefault();
      event.stopPropagation();
      const id = btn.dataset.series as IncomeChartSeriesId | undefined;
      if (!id) return;
      const hidden = readHiddenSeries();
      if (hidden.has(id)) hidden.delete(id);
      else hidden.add(id);
      if (hidden.size >= INCOME_CHART_SERIES.length) hidden.delete(id);
      writeHiddenSeries(hidden);
      redraw();
    });
    const compactMq = window.matchMedia("(max-width: 720px)");
    const onViewportChange = (): void => {
      redraw();
    };
    if (typeof compactMq.addEventListener === "function") {
      compactMq.addEventListener("change", onViewportChange);
    } else {
      compactMq.addListener(onViewportChange);
    }
  }

  const wirePlot = (): void => {
    const svg = host.querySelector<SVGSVGElement>(".sfe-income-chart-svg");
    const tip = host.querySelector<HTMLElement>(".sfe-income-chart-tip");
    const hover = host.querySelector<SVGLineElement>(".sfe-income-chart-hover-x");
    const wrap = host.querySelector<HTMLElement>(".sfe-income-chart-plot-wrap");
    if (!svg || !tip || !hover || !wrap || !points.length) return;

    const hideTip = (): void => {
      tip.hidden = true;
      hover.setAttribute("hidden", "");
    };

    const showAt = (index: number, clientX: number, _clientY?: number): void => {
      const point = points[index];
      if (!point) return hideTip();
      const x = xAt(index, points.length);
      hover.setAttribute("x1", String(x));
      hover.setAttribute("x2", String(x));
      hover.removeAttribute("hidden");
      const hidden = readHiddenSeries();
      const forecast = point.forecast
        ? ` <span class="sfe-income-chart-tip-forecast">Forecast</span>`
        : "";
      tip.innerHTML =
        `<div class="sfe-income-chart-tip-title">${esc(point.label)}${forecast}</div>` +
        tooltipRowsHtml(point, hidden);
      tip.hidden = false;
      tip.style.position = "absolute";
      tip.style.left = "0px";
      tip.style.top = "10px";
      const wrapRect = wrap.getBoundingClientRect();
      // Desktop panes apply CSS zoom on #app/html. getBoundingClientRect/clientX are
      // screen px; style.left is layout px — divide by the wrap's effective scale.
      const layoutW = Math.max(1, wrap.offsetWidth);
      const scaleX = wrapRect.width / layoutW || 1;
      const tipW = tip.offsetWidth;
      const margin = 8;
      const gap = 20;
      const cursorX = (clientX - wrapRect.left) / scaleX;
      const maxLeft = Math.max(margin, layoutW - tipW - margin);
      let left = cursorX + gap;
      if (left > maxLeft) left = cursorX - tipW - gap;
      if (left < margin) left = margin;
      if (left > maxLeft) left = maxLeft;
      tip.style.left = `${Math.round(left)}px`;
      tip.style.top = "10px";
    };

    let pointerKind: "mouse" | "touch" = "mouse";

    svg.addEventListener("selectstart", (event) => event.preventDefault());
    svg.addEventListener("dragstart", (event) => event.preventDefault());
    svg.addEventListener("contextmenu", (event) => event.preventDefault());
    svg.addEventListener("pointerdown", (event) => {
      pointerKind = event.pointerType === "mouse" ? "mouse" : "touch";
      if (pointerKind !== "mouse") event.preventDefault();
      showAt(nearestIndex(svg, event.clientX, points.length), event.clientX, event.clientY);
    });
    svg.addEventListener("pointermove", (event) => {
      showAt(nearestIndex(svg, event.clientX, points.length), event.clientX, event.clientY);
    });
    svg.addEventListener("pointerleave", (event) => {
      if (event.pointerType !== "mouse") return;
      hideTip();
    });
    svg.addEventListener("click", (event) => {
      const finger = pointerKind !== "mouse" || window.matchMedia("(pointer: coarse)").matches;
      if (finger) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!options.onSelectMonth) return;
      const index = nearestIndex(svg, event.clientX, points.length);
      const month = points[index]?.month;
      if (month) options.onSelectMonth(month);
    });
  };

  wirePlot();
}

export function mountIncomeByMonthChart(
  container: HTMLElement,
  points: IncomeChartMonthPoint[],
  options: {
    selectedMonth?: string | null;
    onSelectMonth?: ((month: string) => void) | null;
  } = {},
): HTMLElement {
  let host = container.querySelector<HTMLElement>(".sfe-income-chart");
  if (!host) {
    host = document.createElement("div");
    host.className = "sfe-income-chart";
    container.appendChild(host);
  }
  host.innerHTML = incomeChartInnerHtml(points, options.selectedMonth ?? null);
  bindIncomeByMonthChart(container, {
    points,
    selectedMonth: options.selectedMonth,
    onSelectMonth: options.onSelectMonth,
  });
  return host;
}

export function incomeChartSectionTip(): string {
  return CHART_TIP;
}
